import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Menu', href: '/menu' },
  { label: 'Track Order', href: '/tracking' },
  { label: 'Reviews', href: '/reviews' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const { totalCount, openCart } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textClass = (active?: boolean) =>
    `text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
      active
        ? 'text-[#FF6B35]'
        : scrolled
        ? 'text-gray-700 hover:text-[#FF6B35]'
        : 'text-white hover:text-[#FF6B35]'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/c5283671-54c4-4b1c-8551-32a9e5d65262.png"
            alt="SipDash Logo"
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={textClass(location.pathname === link.href)}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right Actions */}
        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated && user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-[#FF6B35] to-[#e85d2a] text-white text-sm font-bold select-none">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <span className={`text-sm font-medium whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>
                  {user.name.split(' ')[0]}
                </span>
                <div className={`w-4 h-4 flex items-center justify-center ${scrolled ? 'text-gray-500' : 'text-white/70'}`}>
                  <i className={`text-sm transition-transform ${userDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                </div>
              </button>

              {userDropdownOpen && (
                <div className="absolute right-0 top-12 w-52 bg-white border border-gray-100 rounded-xl overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/account"
                    onClick={() => setUserDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-[#fafaf8] cursor-pointer transition-colors"
                  >
                    <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-user-line text-sm" />
                    </div>
                    My Account
                  </Link>
                  <Link
                    to="/account"
                    onClick={() => setUserDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-[#fafaf8] cursor-pointer transition-colors"
                  >
                    <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-shopping-bag-3-line text-sm" />
                    </div>
                    Order History
                  </Link>
                  <button
                    onClick={() => { logout(); setUserDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-colors border-t border-gray-100"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-logout-box-r-line text-sm" />
                    </div>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className={textClass()}>Sign In</Link>
              <Link
                to="/register"
                className="bg-[#FF6B35] text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-[#e85d2a] transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Started
              </Link>
            </>
          )}

          <button onClick={openCart} className="relative cursor-pointer">
            <div className={`w-8 h-8 flex items-center justify-center ${scrolled ? 'text-gray-700' : 'text-white'}`}>
              <i className="ri-shopping-cart-line text-xl" />
            </div>
            {totalCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF6B35] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {totalCount > 9 ? '9+' : totalCount}
              </span>
            )}
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          className={`md:hidden cursor-pointer w-8 h-8 flex items-center justify-center ${scrolled ? 'text-gray-800' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile Dropdown */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-gray-700 font-medium hover:text-[#FF6B35] cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
          {isAuthenticated && user ? (
            <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
              <Link to="/account" onClick={() => setMenuOpen(false)} className="text-gray-700 font-medium cursor-pointer whitespace-nowrap">My Account</Link>
              <button onClick={() => { logout(); setMenuOpen(false); }} className="text-left text-red-500 font-medium cursor-pointer whitespace-nowrap">Sign Out</button>
            </div>
          ) : (
            <div className="flex gap-3 pt-2">
              <Link to="/login" className="text-gray-700 font-medium cursor-pointer whitespace-nowrap" onClick={() => setMenuOpen(false)}>Sign In</Link>
              <Link to="/register" className="bg-[#FF6B35] text-white text-sm font-semibold px-5 py-2 rounded-full cursor-pointer whitespace-nowrap" onClick={() => setMenuOpen(false)}>Get Started</Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
